"use client";

import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { Button, Icon } from "@farm-junction/design-library";
import ColourVariantPanel from "@/component/ui/Colours/ColourVariantPanel";
import ImagePageFilters from "./ImagePageFilters";
import ImagePageCarousel, { ImagePageThumbCarousel, useImageCarousel } from "./ImagePageCarousel";
import { getGalleryCategoryCounts } from "./galleryData";
import { useImagePageMedia } from "./ImagePageMedia";
import type { GalleryCategoryId, ImagePageGalleryData } from "./types";

type LightboxSlide = {
    id: string;
    src: string;
    alt: string;
    title: string;
    label: string;
    category: GalleryCategoryId;
    subcategory: string;
};

type ImagePageLightboxProps = {
    data: ImagePageGalleryData;
    isOpen: boolean;
    imageId?: number | string;
    startCategory?: GalleryCategoryId;
    startSubcategory?: string;
    onClose: () => void;
    onOffersClick?: () => void;
};

const matchesFilter = (
    slide: LightboxSlide,
    category: GalleryCategoryId,
    subcategory: string,
) => {
    // All includes colour slides so its count is images + colours. A sub-category
    // (Front View, Dashboard, ...) stays images only.
    if (slide.category === "colour") {
        return category === "colour" || (category === "all" && subcategory === "all");
    }
    if (category === "colour") return false;
    if (category !== "all" && slide.category !== category) return false;
    if (subcategory !== "all" && slide.subcategory !== subcategory) return false;
    return true;
};

export default function ImagePageLightbox({
    data,
    isOpen,
    imageId,
    startCategory = "all",
    startSubcategory,
    onClose,
    onOffersClick,
}: ImagePageLightboxProps) {
    const [mounted, setMounted] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [popupCategory, setPopupCategory] = useState<GalleryCategoryId>("all");
    const [popupSubcategory, setPopupSubcategory] = useState("all");
    const {
        activeCategory,
        activeSubcategory,
    } = useImagePageMedia();
    const counts = useMemo(() => getGalleryCategoryCounts(data), [data]);

    const slides: LightboxSlide[] = useMemo(() => [
        ...data.images.map((image) => ({
            id: String(image.id),
            src: image.src,
            alt: image.alt,
            title: image.title,
            label: image.label,
            category: image.category as GalleryCategoryId,
            subcategory: image.subcategory,
        })),
        ...data.colours.map((colour) => ({
            id: colour.id,
            src: colour.image,
            alt: colour.alt,
            title: colour.title,
            label: colour.name,
            category: "colour" as GalleryCategoryId,
            subcategory: "all",
        })),
    ], [data]);

    const filteredSlides = useMemo(
        () => slides.filter((slide) => matchesFilter(slide, popupCategory, popupSubcategory)),
        [popupCategory, popupSubcategory, slides],
    );

    const { startIndex, activeIndex, jumpKey, jumpTo, setActiveIndex } = useImageCarousel(filteredSlides.length);
    const current = filteredSlides[activeIndex];
    const isColourTab = popupCategory === "colour";
    const showPopupTabs = data.categories.length > 1 || data.subcategories.some((item) => item.id !== "all");
    const activeColour = isColourTab
        ? data.colours.find((colour) => colour.id === current?.id)
        : undefined;
    const colourVariants = activeColour?.variants ?? [];
    // Colours page wording: "<variants offered in this colour> of <all variants>".
    const totalVariantCount = useMemo(
        () => new Set(data.colours.flatMap((colour) => (colour.variants ?? []).map((variant) => variant.id))).size,
        [data.colours],
    );

    const goToSlide = useCallback((index: number) => {
        jumpTo(index, filteredSlides.length);
    }, [filteredSlides.length, jumpTo]);

    const applyFilter = useCallback((
        category: GalleryCategoryId,
        subcategory: string,
        targetId?: string,
    ) => {
        const nextSlides = slides.filter((slide) => matchesFilter(slide, category, subcategory));
        const matchedIndex = targetId
            ? nextSlides.findIndex((slide) => slide.id === targetId)
            : 0;
        setPopupCategory(category);
        setPopupSubcategory(category === "colour" ? "all" : subcategory);
        jumpTo(matchedIndex >= 0 ? matchedIndex : 0, nextSlides.length);
    }, [jumpTo, slides]);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        const query = window.matchMedia("(max-width: 767px)");
        const sync = () => setIsMobile(query.matches);
        sync();
        query.addEventListener("change", sync);
        return () => query.removeEventListener("change", sync);
    }, []);

    useLayoutEffect(() => {
        if (!isOpen) return;

        if (startCategory === "colour") {
            applyFilter("colour", "all", imageId ? String(imageId) : undefined);
            return;
        }

        const matchedIndex = slides.findIndex((slide) => slide.id === String(imageId ?? ""));
        const matchedSlide = matchedIndex >= 0 ? slides[matchedIndex] : undefined;
        const requestedCategory: GalleryCategoryId =
            startCategory === "all"
                ? "all"
                : startCategory || matchedSlide?.category || activeCategory;
        // Only honour categories / sub-categories the data really has. Gallery
        // built from plain model images has just "All", so anything else falls back to it.
        const nextCategory: GalleryCategoryId = data.categories.some((item) => item.id === requestedCategory)
            ? requestedCategory
            : "all";
        const nextSubcategory =
            nextCategory === "all" && requestedCategory !== "all"
                ? "all"
                : startCategory === "all" || !startSubcategory || startSubcategory === "all"
                  ? "all"
                  : data.subcategories.some((item) => item.id === startSubcategory)
                    ? startSubcategory
                    : "all";

        applyFilter(nextCategory, nextSubcategory, matchedSlide?.id);
    }, [imageId, isOpen, startCategory, startSubcategory]);

    useEffect(() => {
        if (!isOpen) return;

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
            if (event.key === "ArrowLeft") goToSlide(activeIndex - 1);
            if (event.key === "ArrowRight") goToSlide(activeIndex + 1);
        };

        window.addEventListener("keydown", onKeyDown);
        return () => {
            document.body.style.overflow = originalOverflow;
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [activeIndex, goToSlide, isOpen, onClose]);

    if (!mounted || !isOpen) return null;

    return createPortal(
        <div
            className="fixed inset-0 flex flex-col font-sans"
            style={{
                zIndex: 99999,
                background: "#000000E5",
                backdropFilter: "blur(14.8px)",
                WebkitBackdropFilter: "blur(14.8px)",
            }}
            onClick={onClose}
        >
            <div className="flex min-h-0 flex-1 flex-col" onClick={(event) => event.stopPropagation()}>
                <div className="flex shrink-0 justify-end px-2 pt-2 md:absolute md:right-8 md:top-4 md:z-20 md:p-0">
                    <Button
                        variant="ghost"
                        aria-label="Close images"
                        onClick={onClose}
                        className="h-8 w-8 shrink-0 rounded-full border-none !bg-transparent p-0 hover:!bg-white/10 md:h-10 md:w-10"
                    >
                        <span className="md:hidden">
                            <Icon name="close" fill="#ffffff" width={14} height={14} />
                        </span>
                        <span className="hidden md:inline-flex">
                            <Icon name="close" fill="#ffffff" width={20} height={20} />
                        </span>
                    </Button>
                </div>

                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-visible pb-4 md:items-center md:overflow-visible md:px-8 md:pb-8">
                    {showPopupTabs && (
                        <div className="w-full min-w-0 px-4 md:flex md:w-[682px] md:flex-col md:items-center md:px-0">
                            <ImagePageFilters
                                variant="popup"
                                categories={data.categories}
                                subcategories={data.subcategories}
                                counts={counts}
                                activeCategory={popupCategory}
                                activeSubcategory={popupSubcategory}
                                onCategoryChange={(id) => applyFilter(id, "all")}
                                onSubcategoryChange={(id) => applyFilter(popupCategory, id)}
                            />
                        </div>
                    )}

                    {current ? (
                        <div className={`${showPopupTabs ? "mt-[120px] md:mt-6" : "mt-12 md:mt-6"} w-full md:flex md:items-start md:justify-center md:gap-4`}>
                            {isColourTab && colourVariants.length > 0 && (
                                <ColourVariantPanel
                                    variants={colourVariants}
                                    totalCount={data.variantTotal || totalVariantCount}
                                    resetKey={current.id}
                                    onNavigate={onClose}
                                />
                            )}

                            <div className="min-w-0 w-full pl-4 pr-4 md:w-[682px] md:shrink-0 md:pl-0 md:pr-0">
                                <ImagePageCarousel
                                    variant="popup" 
                                    slides={filteredSlides}
                                    startIndex={startIndex}
                                    activeIndex={activeIndex}
                                    jumpKey={jumpKey}
                                    frameClassName="bg-[#1A1A1A]"
                                    sizes="(min-width: 768px) 682px, 100vw"
                                    isMobile={isMobile}
                                    onIndexChange={setActiveIndex}
                                    onNavigate={goToSlide}
                                />

                                {isColourTab ? (
                                    <div className="mt-4 flex items-center gap-2 overflow-x-auto py-1 scrollbar-hide md:mt-5 md:justify-center md:gap-3">
                                        {data.colours.map((colour, index) => {
                                            const isSelected = colour.id === current.id;
                                            return (
                                                <button
                                                    key={colour.id}
                                                    type="button"
                                                    title={colour.name}
                                                    aria-label={`View ${colour.name}`}
                                                    aria-pressed={isSelected}
                                                    onClick={() => goToSlide(index)}
                                                    className={`h-[51px] w-[51px] shrink-0 cursor-pointer rounded-full border-2 p-[2px] shadow-sm md:h-[48px] md:w-[48px] ${
                                                        isSelected ? "border-[#00c2c1]" : "border-gray-300 hover:border-gray-400"
                                                    }`}
                                                >
                                                    <span className="block h-full w-full rounded-full" style={{ background: colour.hex }} />
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="mt-4 w-full min-w-0 md:mt-5">
                                        <ImagePageThumbCarousel
                                            slides={filteredSlides}
                                            activeId={current.id}
                                            onThumbClick={goToSlide}
                                            frameClassName="bg-[#1A1A1A]"
                                            itemsPerView={6.4}
                                            mobileItemsPerView={4.2}
                                        />
                                    </div>
                                )}

                                {isColourTab && colourVariants.length > 0 && (
                                    <div className="mt-3 flex gap-2 overflow-x-auto pb-2 scrollbar-hide md:hidden">
                                        {colourVariants.map((variant) => {
                                            const className = "flex w-[128px] min-h-[76px] shrink-0 flex-col justify-between rounded-xl border border-white/60 px-3 py-2.5 text-left text-white";
                                            const body = (
                                                <>
                                                    <p title={variant.name} className="line-clamp-2 break-words text-[13px] font-semibold leading-[18px]">{variant.name}</p>
                                                    {variant.price ? <p className="mt-1.5 text-[12px] text-white/80">{variant.price}</p> : null}
                                                </>
                                            );
                                            return variant.href ? (
                                                <Link key={variant.id} href={variant.href} prefetch={false} onClick={onClose} className={className}>
                                                    {body}
                                                </Link>
                                            ) : (
                                                <div key={variant.id} className={className}>{body}</div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <p className="mt-10 text-sm text-white/80">No images found in this category.</p>
                    )}
                </div>

                <div className="shrink-0 border-t border-white/10 bg-black/80 px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] md:hidden">
                    <Button
                        variant="ghost"
                        title={data.offersButtonText}
                        aria-label={data.offersButtonText}
                        className="w-full rounded-full border border-white/20 !bg-white py-3 text-base font-medium !text-[#111111] hover:!bg-white"
                        onClick={onOffersClick}
                    >
                        {data.offersButtonText}
                    </Button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
